import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { SydonUser } from '../services/sydonApi';

const BG = '#181B25';
const BORDER = '#CACFD8';
const CHIP_BG = '#2B303B';
const MUTED = '#99A0AE';

export type NavSection = 'tax' | 'ai';

export interface NotifItem {
  id: string;
  type: 'nexus' | 'unclassified';
  title: string;
  body: string;
  onClick: () => void;
}

interface HeaderProps {
  section: NavSection;
  onSectionChange: (section: NavSection) => void;
  user: SydonUser;
  organizationId: string;
  onSettings: () => void;
  onLogout: () => void;
  notifications: NotifItem[];
  onDismissNotif: (id: string) => void;
  onDismissAll: () => void;
}

export default function Header({
  section,
  onSectionChange,
  user,
  organizationId,
  onSettings,
  onLogout,
  notifications,
  onDismissNotif,
  onDismissAll,
}: HeaderProps): JSX.Element {
  const [dark, setDark] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bellOpen && !menuOpen) return;
    function onOutside(e: MouseEvent): void {
      if (bellOpen && bellRef.current && !bellRef.current.contains(e.target as Node))
        setBellOpen(false);
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target as Node))
        setMenuOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [bellOpen, menuOpen]);

  const bellCount = notifications.length;

  return (
    <header
      style={{
        height: 64,
        flexShrink: 0,
        background: BG,
        borderBottom: `1px solid ${BORDER}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: 200 }}>
        <img
          style={{ height: 28, width: 'auto', flexShrink: 0 }}
          src="/assets/sydon-logo-white.png"
          alt="Sydon"
        />
      </div>

      <div style={{ display: 'flex', background: CHIP_BG, borderRadius: 999, padding: 3, gap: 2 }}>
        <button
          type="button"
          onClick={() => onSectionChange('tax')}
          style={pillStyle(section === 'tax')}
        >
          Tax &amp; Compliance
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: 200,
          justifyContent: 'flex-end',
        }}
      >
        <button
          type="button"
          aria-label="Toggle dark mode"
          onClick={() => setDark((d) => !d)}
          style={squareBtnStyle(dark)}
        >
          <MoonIcon />
        </button>

        <div ref={bellRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-label="Notifications"
            onClick={() => setBellOpen((o) => !o)}
            style={squareBtnStyle(bellOpen)}
          >
            <BellIcon />
            {bellCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  minWidth: 14,
                  height: 14,
                  background: '#EF4444',
                  color: '#fff',
                  borderRadius: 7,
                  fontSize: 8,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 3px',
                  boxShadow: `0 0 0 2px ${BG}`,
                  lineHeight: 1,
                }}
              >
                {bellCount}
              </span>
            )}
          </button>

          {bellOpen && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 8px)',
                width: 316,
                background: '#fff',
                border: '1px solid #E1E4EA',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                zIndex: 999,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid #F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: '#0E121B' }}>
                  Notifications
                </span>
                {bellCount > 0 && (
                  <span style={{ fontSize: 10, color: '#94A3B8' }}>{bellCount} unread</span>
                )}
              </div>

              {notifications.length === 0 ? (
                <div
                  style={{
                    padding: '22px 14px',
                    textAlign: 'center',
                    color: '#94A3B8',
                    fontSize: 12,
                  }}
                >
                  All caught up ✓
                </div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '11px 14px',
                      borderBottom: '1px solid #F8FAFC',
                      background: n.type === 'nexus' ? '#FEFAFA' : '#FFFDF5',
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: n.type === 'nexus' ? '#FEE2E2' : '#FEF3C7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      {n.type === 'nexus' ? '🚨' : '📦'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{ fontSize: 11, fontWeight: 700, color: '#0E121B', marginBottom: 2 }}
                      >
                        {n.title}
                      </div>
                      <div style={{ fontSize: 11, color: '#647183', lineHeight: 1.4 }}>
                        {n.body}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          n.onClick();
                          setBellOpen(false);
                        }}
                        style={{
                          marginTop: 5,
                          fontSize: 10,
                          fontWeight: 700,
                          color: n.type === 'nexus' ? '#DC2626' : '#D97706',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        {n.type === 'nexus' ? 'View Nexus Status →' : 'Classify Products →'}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => onDismissNotif(n.id)}
                      aria-label="Dismiss"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#CBD5E1',
                        cursor: 'pointer',
                        padding: '1px 2px',
                        fontSize: 14,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}

              {bellCount > 0 && (
                <div
                  style={{
                    padding: '8px 14px',
                    background: '#F8FAFC',
                    borderTop: '1px solid #F1F5F9',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onDismissAll();
                      setBellOpen(false);
                    }}
                    style={{
                      fontSize: 11,
                      color: '#64748B',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    Dismiss all
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Account menu"
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1B4F8C 0%, #2563EB 100%)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: '#fff',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {user.email.charAt(0).toUpperCase()}
          </button>

          {menuOpen && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 8px)',
                width: 200,
                background: '#fff',
                border: '1px solid #E1E4EA',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                zIndex: 999,
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#0E121B',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.email}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <span style={{ fontSize: 10, color: '#647183', textTransform: 'capitalize' }}>
                    {user.role}
                  </span>
                  <span style={{ fontSize: 10, color: '#CACFD8' }}>·</span>
                  <span style={{ fontSize: 10, color: '#647183' }}>{organizationId}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  onSettings();
                  setMenuOpen(false);
                }}
                style={menuItemStyle}
              >
                Settings
              </button>
              <button
                type="button"
                onClick={() => {
                  onLogout();
                  setMenuOpen(false);
                }}
                style={{ ...menuItemStyle, color: '#DC2626' }}
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: '7px 16px',
    border: 'none',
    borderRadius: 999,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    background: active ? '#fff' : 'transparent',
    color: active ? '#181B25' : MUTED,
  };
}

function squareBtnStyle(active: boolean): React.CSSProperties {
  return {
    position: 'relative',
    width: 34,
    height: 34,
    borderRadius: 8,
    background: CHIP_BG,
    border: active ? '1px solid #3A4152' : '1px solid transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  };
}

const menuItemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '9px 14px',
  fontSize: 12,
  fontWeight: 500,
  color: '#0E121B',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
};

function StacLogo(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <defs>
        <linearGradient
          id="sydon-g-header"
          x1="12"
          y1="0"
          x2="12"
          y2="24"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#00E8FF" />
          <stop offset="100%" stopColor="#3355FF" />
        </linearGradient>
      </defs>
      <path d="M12,1.5 C6,0.5 0.5,6 1.5,12 C4,10 10,4 12,1.5Z" fill="url(#sydon-g-header)" />
      <path d="M12,1.5 C18,0.5 23.5,6 22.5,12 C20,10 14,4 12,1.5Z" fill="url(#sydon-g-header)" />
      <path d="M12,22.5 C6,23.5 0.5,18 1.5,12 C4,14 10,20 12,22.5Z" fill="url(#sydon-g-header)" />
      <path
        d="M12,22.5 C18,23.5 23.5,18 22.5,12 C20,14 14,20 12,22.5Z"
        fill="url(#sydon-g-header)"
      />
    </svg>
  );
}

function MoonIcon(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
    </svg>
  );
}

function BellIcon(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
